<template>
  <main v-if="isConsolidated" class="network-home" aria-labelledby="network-home-title">
    <section class="network-home__hero">
      <div><span><i class="fas fa-layer-group"></i> MODE KONSOLIDASI</span><h1 id="network-home-title">Ringkasan seluruh cabang</h1><p>Pantau kinerja jaringan tanpa masuk ke transaksi operasional cabang.</p></div>
      <div class="network-home__actions"><router-link to="/report"><i class="fas fa-chart-line"></i>Laporan &amp; Analitik</router-link><router-link to="/branch-network"><i class="fas fa-cog"></i>Kelola Jaringan</router-link></div>
    </section>
    <div v-if="hasLoadError" class="home-notice" role="status"><i class="fas fa-exclamation-circle"></i>Ringkasan konsolidasi belum dapat dimuat. Pastikan akun memiliki akses laporan seluruh cabang.</div>
    <section class="network-home__metrics">
      <article><i class="fas fa-clinic-medical"></i><span>Cabang dipantau<strong>{{ loading ? '—' : formatNumber(networkRows.length) }}</strong></span></article>
      <article><i class="fas fa-wallet"></i><span>Pendapatan jaringan<strong>{{ loading ? '—' : formatCurrency(networkTotals.revenue) }}</strong></span></article>
      <article><i class="fas fa-chart-line"></i><span>Laba bersih<strong>{{ loading ? '—' : formatCurrency(networkTotals.net_profit) }}</strong></span></article>
      <article><i class="fas fa-calendar-check"></i><span>Total kunjungan<strong>{{ loading ? '—' : formatNumber(networkTotals.appointments) }}</strong></span></article>
    </section>
    <section class="network-home__panel">
      <header><div><span>PERBANDINGAN CABANG</span><h2>Kinerja periode berjalan</h2></div><button type="button" :disabled="loading" @click="loadConsolidatedDashboard"><i class="fas fa-sync-alt" :class="{'fa-spin':loading}"></i> Perbarui</button></header>
      <div class="network-home__table"><table><thead><tr><th>Cabang</th><th>Pendapatan</th><th>Biaya</th><th>Laba bersih</th><th>Kunjungan</th><th>Pasien</th></tr></thead><tbody><tr v-for="branch in networkRows" :key="branch.id"><td><strong>{{branch.name}}</strong><small>{{branch.code}}</small></td><td>{{formatCurrency(branch.revenue)}}</td><td>{{formatCurrency(branch.expenses)}}</td><td :class="branch.net_profit>=0?'is-positive':'is-negative'">{{formatCurrency(branch.net_profit)}}</td><td>{{formatNumber(branch.appointments)}}</td><td>{{formatNumber(branch.patients)}}</td></tr><tr v-if="!loading&&!networkRows.length"><td colspan="6" class="network-home__empty">Belum ada data cabang yang dapat ditampilkan.</td></tr></tbody></table></div>
    </section>
  </main>
  <main v-else class="clinic-home" aria-labelledby="home-title">
    <section class="home-hero">
      <div class="home-hero__skin-art" aria-hidden="true">
        <img :src="skin.illustration" alt="" />
      </div>
      <div class="home-hero__copy">
        <div class="home-eyebrow"><i></i> {{ skin.eyebrow }}</div>
        <h1 id="home-title">{{ greeting }}, {{ firstName }}</h1>
        <p>{{ clinicName }} · {{ todayLabel }}</p>
        <div class="home-skin-note">
          <span><i :class="skin.icon"></i> {{ skin.name }}</span>
          {{ skin.description }}
        </div>
        <div class="home-hero__actions">
          <router-link :to="{ name: 'appointment-add' }" class="home-btn home-btn--light">
            <i class="fas fa-calendar-plus"></i> Buat perjanjian
          </router-link>
          <router-link :to="{ name: 'patient-add' }" class="home-btn home-btn--ghost">
            <i class="fas fa-user-plus"></i> Daftarkan pasien
          </router-link>
        </div>
      </div>
      <div class="home-hero__summary">
        <div>
          <span>Perjanjian hari ini</span>
          <strong>{{ loading ? "—" : formatNumber(summary.appointmentsToday) }}</strong>
          <small>{{ appointmentSummary }}</small>
        </div>
        <router-link :to="{ name: 'appointment-todays' }" aria-label="Buka perjanjian hari ini">
          <i class="fas fa-arrow-right"></i>
        </router-link>
      </div>
    </section>

    <nav class="home-shortcuts" aria-label="Aksi cepat">
      <router-link v-for="action in quickActions" :key="action.label" :to="action.to" class="home-shortcut">
        <span class="home-shortcut__icon" :class="'is-' + action.tone"><i :class="action.icon"></i></span>
        <span><strong>{{ action.label }}</strong><small>{{ action.description }}</small></span>
        <i class="fas fa-chevron-right home-shortcut__arrow"></i>
      </router-link>
    </nav>

    <section class="home-section" aria-labelledby="overview-title">
      <header class="home-section__head">
        <div>
          <span class="home-kicker">Kondisi hari ini</span>
          <h2 id="overview-title">Yang perlu diketahui sekarang</h2>
        </div>
        <button type="button" class="home-refresh" :disabled="loading" @click="loadDashboard">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          {{ loading ? "Memperbarui" : "Perbarui data" }}
        </button>
      </header>

      <div v-if="hasLoadError" class="home-notice" role="status">
        <i class="fas fa-exclamation-circle"></i>
        Sebagian data belum berhasil dimuat. Data lain yang tersedia tetap ditampilkan.
      </div>

      <div class="home-metrics">
        <article v-for="metric in primaryMetrics" :key="metric.label" class="home-metric">
          <div class="home-metric__top">
            <span class="home-metric__icon" :class="'is-' + metric.tone"><i :class="metric.icon"></i></span>
            <span class="home-chip">{{ metric.context }}</span>
          </div>
          <strong>{{ loading ? "—" : metric.value }}</strong>
          <span>{{ metric.label }}</span>
          <small>{{ metric.helper }}</small>
        </article>
      </div>
    </section>

    <section class="home-grid">
      <article class="home-panel home-panel--revenue">
        <header class="home-panel__head home-panel__head--row">
          <div>
            <span class="home-kicker">Performa bisnis</span>
            <h2>Pendapatan 7 hari terakhir</h2>
            <p>Klinik dan apotek dalam satu pandangan.</p>
          </div>
          <div class="home-revenue-total">
            <span>Total pendapatan</span>
            <strong>{{ loading ? "—" : formatCurrency(summary.totalRevenue) }}</strong>
          </div>
        </header>
        <div class="home-revenue-split">
          <div><span><i class="legend is-clinic"></i>Klinik</span><strong>{{ formatCurrency(summary.clinicRevenue) }}</strong></div>
          <div><span><i class="legend is-pharmacy"></i>Apotek</span><strong>{{ formatCurrency(summary.pharmacyRevenue) }}</strong></div>
        </div>
        <div v-if="loading" class="home-skeleton home-skeleton--chart"></div>
        <apexchart v-else type="area" height="250" :options="chartOptions" :series="revenueSeries" />
      </article>

      <aside class="home-panel">
        <header class="home-panel__head">
          <span class="home-kicker">Fokus hari ini</span>
          <h2>Prioritas operasional</h2>
          <p>Jalur tercepat untuk menjaga layanan tetap lancar.</p>
        </header>
        <div class="home-focus">
          <router-link v-for="item in focusItems" :key="item.title" :to="item.to" class="home-focus__item">
            <span class="home-focus__icon" :class="'is-' + item.tone"><i :class="item.icon"></i></span>
            <span><strong>{{ item.title }}</strong><small>{{ item.description }}</small></span>
            <b>{{ loading ? "—" : formatNumber(item.value) }}</b>
          </router-link>
        </div>
        <div class="home-system">
          <span class="home-system__icon"><i class="fas fa-shield-alt"></i></span>
          <div><strong>Sistem siap melayani</strong><small>Data operasional tersinkron otomatis</small></div>
          <span class="home-status"><i></i> Aktif</span>
        </div>
      </aside>
    </section>

    <section class="home-grid home-grid--agenda">
      <article class="home-panel">
        <header class="home-panel__head home-panel__head--row">
          <div>
            <span class="home-kicker">Agenda klinis</span>
            <h2>Perjanjian berikutnya</h2>
            <p>{{ formatNumber(summary.appointmentsToday) }} jadwal terdaftar hari ini.</p>
          </div>
          <router-link :to="{ name: 'appointment-todays' }" class="home-link">Lihat semua <i class="fas fa-arrow-right"></i></router-link>
        </header>
        <div v-if="loading" class="home-loading-list">
          <div v-for="n in 4" :key="n" class="home-skeleton home-skeleton--row"></div>
        </div>
        <div v-else-if="appointments.length" class="home-schedule">
          <router-link
            v-for="item in appointments"
            :key="item.id || item.display_time + item.display_patient_name"
            :to="appointmentRoute(item)"
            class="home-schedule__item"
          >
            <time>{{ item.display_time || "--:--" }}</time><i class="home-schedule__dot"></i>
            <span>
              <strong>{{ item.display_patient_name || item.patient_name || "Pasien" }}</strong>
              <small>{{ item.doctor_name || "Dokter belum ditentukan" }} · {{ item.action_type_name || "Konsultasi" }}</small>
            </span>
            <em>Terjadwal</em>
          </router-link>
        </div>
        <div v-else class="home-empty">
          <span><i class="far fa-calendar-check"></i></span>
          <strong>Belum ada perjanjian hari ini</strong>
          <p>Tambahkan jadwal baru atau lihat kalender untuk mengatur kapasitas layanan.</p>
          <router-link :to="{ name: 'appointment-add' }">Buat perjanjian</router-link>
        </div>
      </article>

      <article class="home-panel">
        <header class="home-panel__head home-panel__head--row">
          <div>
            <span class="home-kicker">Tim hari ini</span>
            <h2>Dokter praktik</h2>
            <p>{{ formatNumber(summary.doctorsToday) }} dokter dijadwalkan.</p>
          </div>
          <router-link :to="{ name: 'schedule-list' }" class="home-link">Jadwal <i class="fas fa-arrow-right"></i></router-link>
        </header>
        <div v-if="loading" class="home-loading-list">
          <div v-for="n in 4" :key="n" class="home-skeleton home-skeleton--row"></div>
        </div>
        <div v-else-if="doctors.length" class="home-doctors">
          <div v-for="doctor in doctors" :key="doctor.id || doctor.doctor_name" class="home-doctors__item">
            <span class="home-avatar">{{ initials(doctor.doctor_name) }}</span>
            <span><strong>{{ doctor.doctor_name || "Dokter" }}</strong><small>{{ doctor.policlinic_name || "Poliklinik" }}</small></span>
            <em><i></i> Praktik</em>
          </div>
        </div>
        <div v-else class="home-empty home-empty--small">
          <span><i class="fas fa-user-md"></i></span>
          <strong>Belum ada jadwal praktik</strong>
          <p>Atur jadwal dokter agar tim front office melihat kapasitas hari ini.</p>
        </div>
      </article>
    </section>

    <section class="home-panel home-panel--invoice">
      <header class="home-panel__head home-panel__head--row">
        <div>
          <span class="home-kicker">Transaksi terbaru</span>
          <h2>Invoice hari ini</h2>
          <p>{{ formatNumber(summary.invoicesToday) }} invoice tercatat dari layanan klinik dan apotek.</p>
        </div>
        <router-link :to="{ name: 'payment-list' }" class="home-link">Buka pembayaran <i class="fas fa-arrow-right"></i></router-link>
      </header>
      <div v-if="loading" class="home-loading-list">
        <div v-for="n in 3" :key="n" class="home-skeleton home-skeleton--row"></div>
      </div>
      <div v-else-if="invoices.length" class="home-table-wrap">
        <table class="home-table">
          <thead><tr><th>Pasien</th><th>Dokter / layanan</th><th>Metode</th><th class="text-right">Total</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="item in invoices" :key="item.id || item.patient_name + item.final_amount">
              <td><strong>{{ item.patient_name || "Pasien umum" }}</strong></td>
              <td>{{ item.doctor_name || item.feature || "-" }}</td>
              <td>{{ item.payment_method_name || "-" }}</td>
              <td class="text-right"><strong>{{ formatCurrency(item.final_amount) }}</strong></td>
              <td><span class="home-paid"><i></i> Tercatat</span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="home-empty home-empty--horizontal">
        <span><i class="fas fa-receipt"></i></span>
        <div><strong>Belum ada invoice hari ini</strong><p>Transaksi yang selesai akan otomatis muncul di sini.</p></div>
        <router-link :to="{ name: 'payment-list' }">Kelola pembayaran</router-link>
      </div>
    </section>
  </main>
</template>

<script>
import moment from "moment";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ApiService from "@/core/services/api.service.js";
import module from "@/core/modules/CrudModule.js";
import VerticalSkinService from "@/core/services/vertical-skin.service.js";
import { getConfiguration, getUser, getdbconfigparams } from "@/core/services/jwt.service.js";

export default {
  name: "dashboard",
  data() {
    return {
      loading: true,
      skin: VerticalSkinService.current(),
      hasLoadError: false,
      appointments: [],
      doctors: [],
      invoices: [],
      clinicRevenueTrend: [],
      pharmacyRevenueTrend: [],
      networkRows: [],
      networkTotals: { revenue: 0, expenses: 0, net_profit: 0, appointments: 0, patients: 0 },
      summary: {
        appointmentsToday: 0,
        doctorsToday: 0,
        patientsMonth: 0,
        invoicesMonth: 0,
        pharmacyOrdersMonth: 0,
        invoicesToday: 0,
        clinicRevenue: 0,
        pharmacyRevenue: 0,
        totalRevenue: 0,
      },
    };
  },
  computed: {
    isConsolidated() {
      return window.localStorage.getItem("medhop_branch_id") === "all";
    },
    clinicName() {
      const configuration = getConfiguration() || {};
      return configuration.clinic_name || "Klinik Anda";
    },
    firstName() {
      const user = getUser() || {};
      const name = user.name || user.full_name || user.username || "Tim Klinik";
      return String(name).trim().split(/\s+/)[0];
    },
    greeting() {
      const hour = Number(moment().format("H"));
      if (hour < 11) return "Selamat pagi";
      if (hour < 15) return "Selamat siang";
      if (hour < 19) return "Selamat sore";
      return "Selamat malam";
    },
    todayLabel() {
      return new Intl.DateTimeFormat("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date());
    },
    appointmentSummary() {
      if (this.loading) return "Menyiapkan ringkasan layanan";
      if (!this.summary.appointmentsToday) return "Belum ada jadwal, kapasitas masih tersedia";
      return this.summary.doctorsToday + " dokter siap melayani pasien";
    },
    quickActions() {
      return [
        { label: "Pasien baru", description: "Registrasi cepat", icon: "fas fa-user-plus", tone: "blue", to: { name: "patient-add" } },
        { label: "Perjanjian", description: "Buat jadwal kunjungan", icon: "fas fa-calendar-plus", tone: "teal", to: { name: "appointment-add" } },
        { label: "Pembayaran", description: "Buka daftar invoice", icon: "fas fa-cash-register", tone: "amber", to: { name: "payment-list" } },
        { label: "Kasir apotek", description: "Proses penjualan obat", icon: "fas fa-pills", tone: "violet", to: { name: "pharmacy-cashier" } },
      ];
    },
    primaryMetrics() {
      return [
        { label: "Perjanjian hari ini", value: this.formatNumber(this.summary.appointmentsToday), helper: this.summary.appointmentsToday ? "Siap dipantau oleh front office" : "Kapasitas layanan masih tersedia", context: "Hari ini", icon: "far fa-calendar-check", tone: "blue" },
        { label: "Dokter praktik", value: this.formatNumber(this.summary.doctorsToday), helper: this.summary.doctorsToday ? "Tim terjadwal hari ini" : "Jadwal belum tersedia", context: "Aktif", icon: "fas fa-user-md", tone: "teal" },
        { label: "Pasien baru", value: this.formatNumber(this.summary.patientsMonth), helper: "Pertumbuhan pasien bulan berjalan", context: "Bulan ini", icon: "fas fa-users", tone: "violet" },
        { label: "Pendapatan 7 hari", value: this.formatCurrencyCompact(this.summary.totalRevenue), helper: "Akumulasi klinik dan apotek", context: "7 hari", icon: "fas fa-chart-line", tone: "amber" },
      ];
    },
    focusItems() {
      return [
        { title: "Jadwal pasien", description: this.summary.appointmentsToday ? "Pastikan layanan tepat waktu" : "Kapasitas masih tersedia", value: this.summary.appointmentsToday, icon: "fas fa-calendar-check", tone: "blue", to: { name: "appointment-todays" } },
        { title: "Invoice bulan ini", description: "Pantau penyelesaian transaksi", value: this.summary.invoicesMonth, icon: "fas fa-file-invoice", tone: "amber", to: { name: "payment-list" } },
        { title: "Pesanan apotek", description: "Jaga pelayanan farmasi tetap cepat", value: this.summary.pharmacyOrdersMonth, icon: "fas fa-prescription-bottle-alt", tone: "violet", to: { name: "pharmacy-sales" } },
      ];
    },
    revenueSeries() {
      return [
        { name: "Klinik", data: this.normalizedTrend(this.clinicRevenueTrend) },
        { name: "Apotek", data: this.normalizedTrend(this.pharmacyRevenueTrend) },
      ];
    },
    chartOptions() {
      const categories = Array.from({ length: 7 }, (_, i) => moment().subtract(6 - i, "days").format("DD MMM"));
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "Poppins, Inter, sans-serif" },
        colors: this.skin.palette,
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.22, opacityTo: 0.02, stops: [0, 90, 100] } },
        grid: { borderColor: "#E8EEF4", strokeDashArray: 4, padding: { left: 8, right: 8 } },
        xaxis: { categories, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#718096", fontSize: "11px" } } },
        yaxis: { labels: { formatter: value => this.formatCurrencyCompact(value), style: { colors: "#718096", fontSize: "11px" } } },
        legend: { show: false },
        tooltip: { shared: true, y: { formatter: value => this.formatCurrency(value) } },
        markers: { size: 0, hover: { size: 5 } },
      };
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.isConsolidated ? "Konsolidasi Jaringan" : "Beranda" }]);
    window.addEventListener("medhop:vertical-skin", this.handleVerticalSkin);
    if (this.isConsolidated) this.loadConsolidatedDashboard();
    else this.loadDashboard();
  },
  beforeDestroy() {
    window.removeEventListener("medhop:vertical-skin", this.handleVerticalSkin);
  },
  methods: {
    handleVerticalSkin(event) {
      this.skin = (event && event.detail) || VerticalSkinService.current();
    },
    async loadConsolidatedDashboard() {
      this.loading = true;
      this.hasLoadError = false;
      try {
        const response = await ApiService.get("branches/dashboard/consolidated");
        const data = response.data.data || {};
        this.networkRows = data.branches || [];
        this.networkTotals = { ...this.networkTotals, ...(data.totals || {}) };
      } catch (error) {
        this.hasLoadError = true;
      } finally {
        this.loading = false;
      }
    },
    async loadDashboard() {
      this.loading = true;
      this.hasLoadError = false;
      const dbParams = getdbconfigparams();
      const results = await Promise.all([
        this.safeGet("dashboard-monthly"),
        this.safeGet("payments-dashboard-income"),
        this.safeGet("pharmacy-dashboard-income"),
        this.safeGet("payments-dashboard-graphic"),
        this.safeGet("pharmacy-dashboard-graphic"),
        this.safeGet("schedules-length-today"),
        this.safePaginate("appointments-today/today", "?page=1&page_size=5"),
        this.safePaginate("schedules-today", "?page=1&page_size=5"),
        this.safePaginate("payments-pharmacy-dashboard-income", "?page=1&page_size=6"),
        ApiService.get("total-appointments-today", dbParams ? "?" + dbParams : "")
          .then(response => response && response.data)
          .catch(() => { this.hasLoadError = true; return 0; }),
      ]);
      const monthly = results[0] && typeof results[0] === "object" ? results[0] : {};
      const clinicIncome = Array.isArray(results[1]) ? results[1] : [];
      const pharmacyIncome = Array.isArray(results[2]) ? results[2] : [];
      this.summary.patientsMonth = this.toNumber(monthly.patient);
      this.summary.invoicesMonth = this.toNumber(monthly.payment);
      this.summary.pharmacyOrdersMonth = this.toNumber(monthly.pharmacy);
      this.summary.clinicRevenue = this.sumAmount(clinicIncome);
      this.summary.pharmacyRevenue = this.sumAmount(pharmacyIncome);
      this.summary.totalRevenue = this.summary.clinicRevenue + this.summary.pharmacyRevenue;
      this.clinicRevenueTrend = Array.isArray(results[3]) ? results[3] : [];
      this.pharmacyRevenueTrend = Array.isArray(results[4]) ? results[4] : [];
      this.summary.doctorsToday = Array.isArray(results[5]) ? results[5].length : this.toNumber(results[5]);
      this.appointments = this.paginatedData(results[6]);
      this.doctors = this.paginatedData(results[7]);
      this.invoices = this.paginatedData(results[8]);
      this.summary.invoicesToday = this.paginationTotal(results[8], this.invoices.length);
      this.summary.appointmentsToday = this.toNumber(results[9]) || this.paginationTotal(results[6], this.appointments.length);
      this.loading = false;
    },
    async safeGet(route) {
      try {
        const response = await module.get(route);
        if (response === null || typeof response === "undefined") this.hasLoadError = true;
        return response;
      } catch (error) {
        this.hasLoadError = true;
        return null;
      }
    },
    async safePaginate(route, query) {
      try {
        const response = await module.paginate(route, query);
        if (!response || response.error) this.hasLoadError = true;
        return response || {};
      } catch (error) {
        this.hasLoadError = true;
        return {};
      }
    },
    paginatedData(response) {
      return response && Array.isArray(response.data) ? response.data : [];
    },
    paginationTotal(response, fallback) {
      const pagination = response && response.meta && response.meta.pagination;
      return pagination ? this.toNumber(pagination.total) : fallback;
    },
    sumAmount(data) {
      return Array.isArray(data) ? data.reduce((sum, item) => sum + this.toNumber(item && item.final_amount), 0) : 0;
    },
    normalizedTrend(data) {
      const values = {};
      (Array.isArray(data) ? data : []).forEach(item => {
        if (item && item.display_created_at) values[item.display_created_at] = this.toNumber(item.final_amount);
      });
      return Array.from({ length: 7 }, (_, i) => values[moment().subtract(6 - i, "days").format("DD-MM-YYYY")] || 0);
    },
    appointmentRoute(item) {
      return item && item.id ? { name: "appointment-detail", params: { id: item.id } } : { name: "appointment-todays" };
    },
    initials(name) {
      return String(name || "DR").split(/\s+/).filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
    },
    toNumber(value) {
      const result = Number(value);
      return Number.isFinite(result) ? result : 0;
    },
    formatNumber(value) {
      return this.toNumber(value).toLocaleString("id-ID");
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(this.toNumber(value));
    },
    formatCurrencyCompact(value) {
      const amount = this.toNumber(value);
      if (amount >= 1000000000) return "Rp " + (amount / 1000000000).toLocaleString("id-ID", { maximumFractionDigits: 1 }) + " M";
      if (amount >= 1000000) return "Rp " + (amount / 1000000).toLocaleString("id-ID", { maximumFractionDigits: 1 }) + " jt";
      if (amount >= 1000) return "Rp " + (amount / 1000).toLocaleString("id-ID", { maximumFractionDigits: 0 }) + " rb";
      return "Rp " + this.formatNumber(amount);
    },
  },
};
</script>

<style lang="scss" scoped>
.network-home{--ink:#173b52;--muted:#708696;--brand:var(--skin-primary);--line:#dfe9ed;padding:0 8px 34px;width:100%}.network-home__hero{align-items:center;background:linear-gradient(125deg,var(--skin-deep),var(--skin-primary) 58%,var(--skin-secondary));border-radius:24px;box-shadow:0 22px 50px rgba(16,47,87,.2);color:#fff;display:flex;justify-content:space-between;min-height:190px;overflow:hidden;padding:32px 38px;position:relative}.network-home__hero span{color:#aeece1;font-size:11px;font-weight:800;letter-spacing:.1em}.network-home__hero span i{margin-right:7px}.network-home__hero h1{color:#fff!important;font-size:32px;margin:9px 0 6px}.network-home__hero p{color:rgba(236,247,252,.76);margin:0}.network-home__actions{display:flex;gap:9px;position:relative;z-index:1}.network-home__actions a{align-items:center;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.22);border-radius:11px;color:#fff;display:flex;font-weight:700;gap:8px;padding:11px 14px}.network-home__actions a:hover{background:rgba(255,255,255,.2);text-decoration:none}.network-home__metrics{display:grid;gap:14px;grid-template-columns:repeat(4,1fr);margin:18px 0}.network-home__metrics article{align-items:center;background:#fff;border:1px solid var(--line);border-radius:15px;display:flex;gap:13px;padding:18px}.network-home__metrics article>i{background:var(--skin-soft);border-radius:11px;color:var(--brand);display:grid;flex:0 0 42px;height:42px;place-items:center}.network-home__metrics span{color:var(--muted);font-size:11px}.network-home__metrics strong{color:var(--ink);display:block;font-size:19px;margin-top:4px}.network-home__panel{background:#fff;border:1px solid var(--line);border-radius:16px;overflow:hidden}.network-home__panel>header{align-items:center;border-bottom:1px solid var(--line);display:flex;justify-content:space-between;padding:17px 20px}.network-home__panel header span{color:var(--brand);font-size:10px;font-weight:800;letter-spacing:.08em}.network-home__panel h2{color:var(--ink);font-size:18px;margin:4px 0 0}.network-home__panel button{background:#fff;border:1px solid var(--line);border-radius:9px;color:var(--ink);font-weight:700;padding:8px 11px}.network-home__table{overflow:auto}.network-home__table table{border-collapse:collapse;width:100%}.network-home__table th{background:#f7fafb;color:var(--muted);font-size:10px;letter-spacing:.05em;padding:11px 14px;text-align:left;text-transform:uppercase}.network-home__table td{border-top:1px solid #edf2f4;color:#415d6d;padding:13px 14px}.network-home__table td strong,.network-home__table td small{display:block}.network-home__table td small{color:var(--muted);margin-top:2px}.network-home__table .is-positive{color:#16815b;font-weight:800}.network-home__table .is-negative{color:#c4414b;font-weight:800}.network-home__empty{text-align:center}.network-home>.home-notice{margin:16px 0 0}@media(max-width:1000px){.network-home__hero{align-items:flex-start;flex-direction:column;gap:22px}.network-home__metrics{grid-template-columns:1fr 1fr}}@media(max-width:620px){.network-home__metrics{grid-template-columns:1fr}.network-home__actions{flex-direction:column;width:100%}.network-home__actions a{justify-content:center}}
.clinic-home {
  --navy: var(--skin-deep, #102f57); --blue: var(--skin-primary, #1676a3); --teal: var(--skin-secondary, #168b82); --text: #172638;
  --muted: #6b7c91; --border: #e3eaf1;
  width:100%; max-width:100%; overflow-x:hidden; box-sizing:border-box; padding:0 8px 34px; color:var(--text); font-family:"Poppins", "Inter", sans-serif;
}
.home-hero {
  position: relative; display: grid; grid-template-columns: minmax(0,1fr) minmax(150px,190px) minmax(280px,390px);
  gap: clamp(20px,2.2vw,34px); align-items: center; overflow: hidden; min-height: 244px; padding: 36px 40px; color: #fff;
  background: radial-gradient(circle at 86% 10%,rgba(80,218,204,.23),transparent 28%),
    radial-gradient(circle at 58% 110%,rgba(58,143,205,.32),transparent 35%),
    linear-gradient(125deg,var(--skin-deep) 0%,var(--skin-primary) 55%,var(--skin-secondary) 100%);
  border-radius: 24px; box-shadow: 0 22px 50px rgba(16,47,87,.2);
}
.home-hero::after { position:absolute; right:-95px; top:-130px; width:330px; height:330px; border:1px solid rgba(255,255,255,.12); border-radius:50%; content:""; }
.home-hero__copy,.home-hero__summary { position:relative; z-index:1; }
.home-hero__copy { grid-column:1; }
.home-hero__summary { grid-column:3; }
.home-eyebrow { display:inline-flex; align-items:center; gap:8px; margin-bottom:12px; color:#c6f4ed; font-size:11px; font-weight:700; letter-spacing:.11em; text-transform:uppercase; }
.home-eyebrow i { width:8px; height:8px; background:#62e6c9; border:2px solid rgba(255,255,255,.28); border-radius:50%; box-shadow:0 0 0 5px rgba(98,230,201,.12); }
.home-hero h1 { max-width:700px; margin:0; color:#fff !important; font-size:clamp(25px,3vw,38px); font-weight:700; line-height:1.2; letter-spacing:-.045em; }
.home-hero__copy>p { margin:9px 0 0; color:rgba(236,247,252,.76) !important; font-size:12px; text-transform:capitalize; }
.home-hero__actions { display:flex; flex-wrap:wrap; gap:10px; margin-top:26px; }
.home-btn { display:inline-flex; align-items:center; justify-content:center; gap:9px; min-height:43px; padding:0 18px; border:1px solid transparent; border-radius:11px; font-size:13px; font-weight:700; transition:.18s ease; }
.home-btn:hover { transform:translateY(-2px); text-decoration:none; }
.home-btn--light { color:var(--navy); background:#fff; box-shadow:0 10px 24px rgba(0,0,0,.16); }
.home-btn--light:hover { color:var(--navy); }
.home-btn--ghost { color:#fff; background:rgba(255,255,255,.08); border-color:rgba(255,255,255,.23); }
.home-btn--ghost:hover { color:#fff; background:rgba(255,255,255,.15); }
.home-hero__summary { display:flex; align-items:center; justify-content:space-between; gap:18px; padding:24px; background:rgba(255,255,255,.1); border:1px solid rgba(255,255,255,.18); border-radius:18px; backdrop-filter:blur(10px); }
.home-hero__summary>div { display:flex; flex-direction:column; }
.home-hero__summary span { color:rgba(255,255,255,.72) !important; font-size:12px; font-weight:600; }
.home-hero__summary strong { margin:6px 0 4px; color:#fff !important; font-size:36px; line-height:1; letter-spacing:-.04em; }
.home-hero__summary small { color:rgba(255,255,255,.68) !important; font-size:11px; line-height:1.5; }
.home-hero__summary>a { display:grid; place-items:center; width:42px; height:42px; flex:0 0 42px; color:#fff; background:rgba(255,255,255,.13); border-radius:50%; }
.home-hero__summary>a:hover { background:rgba(255,255,255,.22); text-decoration:none; }
.home-shortcuts { position:relative; z-index:2; display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; margin:-18px 22px 30px; }
.home-shortcut { display:flex; align-items:center; gap:12px; min-width:0; padding:15px 16px; color:var(--text); background:#fff; border:1px solid var(--border); border-radius:16px; box-shadow:0 12px 30px rgba(16,47,87,.08); transition:.18s ease; }
.home-shortcut:hover { color:var(--text); border-color:#cad8e5; box-shadow:0 16px 34px rgba(16,47,87,.12); transform:translateY(-3px); text-decoration:none; }
.home-shortcut>span:nth-child(2) { display:flex; flex:1; min-width:0; flex-direction:column; }
.home-shortcut strong,.home-shortcut small { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.home-shortcut strong { font-size:13px; }.home-shortcut small { margin-top:3px; color:var(--muted); font-size:11px; }
.home-shortcut__icon,.home-metric__icon { display:grid; place-items:center; width:40px; height:40px; flex:0 0 40px; border-radius:12px; font-size:14px; }
.is-blue { color:#1676a3; background:#eaf5fa; }.is-teal { color:#138378; background:#e9f8f5; }
.is-amber { color:#bd7104; background:#fff4dd; }.is-violet { color:#7651c7; background:#f1edff; }
.home-shortcut__arrow { color:#a9b5c3; font-size:9px; }
.home-section { margin-bottom:24px; }.home-section__head,.home-panel__head--row { display:flex; align-items:flex-start; justify-content:space-between; gap:18px; }
.home-kicker { display:block; margin-bottom:4px; color:var(--teal); font-size:11px; font-weight:750; letter-spacing:.1em; text-transform:uppercase; }
.home-section h2,.home-panel h2 { margin:0; color:var(--text); font-size:18px; font-weight:700; letter-spacing:-.025em; }
.home-panel__head { margin-bottom:18px; }.home-panel__head p { margin:5px 0 0; color:var(--muted); font-size:12px; line-height:1.5; }
.home-refresh { display:inline-flex; align-items:center; gap:8px; padding:9px 13px; color:#52677e; background:#fff; border:1px solid var(--border); border-radius:10px; font-size:12px; font-weight:650; }
.home-notice { display:flex; align-items:center; gap:9px; margin-top:14px; padding:11px 14px; color:#95600f; background:#fff9eb; border:1px solid #f4dfb2; border-radius:10px; font-size:12px; }
.home-metrics { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px; margin-top:15px; }
.home-metric { min-width:0; padding:20px; background:#fff; border:1px solid var(--border); border-radius:17px; box-shadow:0 8px 24px rgba(16,47,87,.045); }
.home-metric__top { display:flex; align-items:center; justify-content:space-between; gap:12px; }
.home-chip { padding:4px 8px; color:#718096; background:#f6f8fa; border-radius:999px; font-size:10px; font-weight:700; }
.home-metric>strong { display:block; margin:16px 0 2px; overflow:hidden; color:var(--text); font-size:28px; font-weight:720; letter-spacing:-.045em; text-overflow:ellipsis; white-space:nowrap; }
.home-metric>span { display:block; color:#40546a; font-size:13px; font-weight:650; }.home-metric>small { display:block; margin-top:5px; color:#8a99aa; font-size:11px; line-height:1.5; }
.home-grid { display:grid; grid-template-columns:minmax(0,1.75fr) minmax(300px,.85fr); gap:16px; margin-bottom:16px; }
.home-grid--agenda { grid-template-columns:minmax(0,1.35fr) minmax(300px,.85fr); }
.home-panel { min-width:0; padding:22px; background:#fff; border:1px solid var(--border); border-radius:18px; box-shadow:0 8px 24px rgba(16,47,87,.045); }
.home-revenue-total { display:flex; flex-direction:column; align-items:flex-end; flex:0 0 auto; }
.home-revenue-total span { color:var(--muted); font-size:11px; }.home-revenue-total strong { margin-top:4px; color:var(--navy); font-size:21px; letter-spacing:-.035em; }
.home-revenue-split { display:flex; gap:28px; margin-bottom:8px; }.home-revenue-split>div { display:flex; flex-direction:column; }
.home-revenue-split span { display:flex; align-items:center; gap:7px; color:var(--muted); font-size:11px; }.home-revenue-split strong { margin-top:3px; color:#34485d; font-size:13px; }
.legend { display:inline-block; width:8px; height:8px; border-radius:50%; }.legend.is-clinic { background:#1676a3; }.legend.is-pharmacy { background:#8b5cf6; }
.home-focus { display:grid; gap:8px; }
.home-focus__item { display:grid; grid-template-columns:38px minmax(0,1fr) auto; align-items:center; gap:11px; padding:11px; color:var(--text); background:#f8fafc; border:1px solid #edf1f5; border-radius:12px; transition:.16s ease; }
.home-focus__item:hover { color:var(--text); background:#f2f7fa; transform:translateX(2px); text-decoration:none; }
.home-focus__icon { display:grid; place-items:center; width:38px; height:38px; border-radius:11px; font-size:12px; }
.home-focus__item>span:nth-child(2) { min-width:0; }.home-focus__item strong,.home-focus__item small { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.home-focus__item strong { font-size:12px; }.home-focus__item small { margin-top:3px; color:var(--muted); font-size:10px; }
.home-focus__item b { display:grid; place-items:center; min-width:30px; height:30px; padding:0 7px; color:var(--navy); background:#fff; border:1px solid var(--border); border-radius:9px; font-size:11px; }
.home-system { display:grid; grid-template-columns:38px minmax(0,1fr) auto; align-items:center; gap:10px; margin-top:12px; padding:13px; color:#d9eef4; background:linear-gradient(130deg,#153b60,#11636e); border-radius:13px; }
.home-system__icon { display:grid; place-items:center; width:38px; height:38px; color:#9fe6d9; background:rgba(255,255,255,.1); border-radius:11px; }
.home-system div { min-width:0; }.home-system strong,.home-system small { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.home-system strong { color:#fff; font-size:12px; }.home-system small { margin-top:2px; color:rgba(231,246,249,.65); font-size:10px; }
.home-status,.home-doctors__item em,.home-paid { display:inline-flex; align-items:center; gap:5px; font-size:9px; font-style:normal; font-weight:700; }
.home-status i,.home-doctors__item em i,.home-paid i { width:6px; height:6px; background:#52dfad; border-radius:50%; }
.home-link { align-self:center; flex:0 0 auto; color:var(--blue); font-size:11px; font-weight:700; }.home-link i { margin-left:5px; font-size:9px; }.home-link:hover { color:var(--navy); text-decoration:none; }
.home-schedule__item { display:grid; grid-template-columns:48px 10px minmax(0,1fr) auto; align-items:center; gap:10px; padding:12px 4px; color:var(--text); border-bottom:1px solid #edf1f5; }
.home-schedule__item:last-child,.home-doctors__item:last-child { border-bottom:0; }.home-schedule__item:hover { color:var(--text); background:#fbfcfd; text-decoration:none; }
.home-schedule__item time { color:var(--navy); font-size:12px; font-weight:750; }.home-schedule__dot { width:8px; height:8px; background:#37a99b; border:2px solid var(--skin-soft); border-radius:50%; }
.home-schedule__item>span { min-width:0; }.home-schedule__item strong,.home-schedule__item small { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.home-schedule__item strong { font-size:12px; }.home-schedule__item small { margin-top:3px; color:var(--muted); font-size:10px; }
.home-schedule__item em { padding:4px 7px; color:#147c72; background:#eaf8f5; border-radius:999px; font-size:9px; font-style:normal; font-weight:700; }
.home-doctors__item { display:grid; grid-template-columns:38px minmax(0,1fr) auto; align-items:center; gap:10px; padding:11px 3px; border-bottom:1px solid #edf1f5; }
.home-avatar { display:grid; place-items:center; width:38px; height:38px; color:#176b8d; background:linear-gradient(145deg,#e6f3f7,#eef8f6); border:1px solid #d5e8ed; border-radius:12px; font-size:11px; font-weight:750; }
.home-doctors__item>span:nth-child(2) { min-width:0; }.home-doctors__item strong,.home-doctors__item small { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.home-doctors__item strong { font-size:12px; }.home-doctors__item small { margin-top:3px; color:var(--muted); font-size:10px; }.home-doctors__item em,.home-paid { color:#147c58; }
.home-table-wrap { overflow-x:auto; }.home-table { width:100%; border-collapse:collapse; }
.home-table th { padding:10px 12px; color:#8290a1; background:#f8fafc; border-bottom:1px solid var(--border); font-size:10px; font-weight:700; letter-spacing:.06em; text-align:left; text-transform:uppercase; white-space:nowrap; }
.home-table th:first-child { border-radius:9px 0 0 9px; }.home-table th:last-child { border-radius:0 9px 9px 0; }
.home-table td { padding:14px 12px; color:#53677c; border-bottom:1px solid #edf1f5; font-size:12px; white-space:nowrap; }.home-table tbody tr:last-child td { border-bottom:0; }.home-table td strong { color:var(--text); }
.home-empty { display:flex; min-height:230px; align-items:center; justify-content:center; flex-direction:column; padding:26px; text-align:center; }
.home-empty>span { display:grid; place-items:center; width:48px; height:48px; margin-bottom:12px; color:#4784a0; background:#edf6f8; border-radius:15px; font-size:17px; }
.home-empty strong { font-size:12px; }.home-empty p { max-width:330px; margin:5px 0 10px; color:var(--muted); font-size:11px; line-height:1.6; }.home-empty>a { color:var(--blue); font-size:11px; font-weight:700; }
.home-empty--small { min-height:205px; }.home-empty--horizontal { min-height:100px; flex-direction:row; gap:14px; justify-content:flex-start; text-align:left; }
.home-empty--horizontal>span { flex:0 0 48px; margin:0; }.home-empty--horizontal div { flex:1; }.home-empty--horizontal p { margin-bottom:0; }
.home-loading-list { display:grid; gap:10px; }.home-skeleton { position:relative; overflow:hidden; background:#edf2f6; border-radius:10px; }
.home-skeleton::after { position:absolute; inset:0; background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent); content:""; transform:translateX(-100%); animation:home-shimmer 1.4s infinite; }
.home-skeleton--row { height:51px; }.home-skeleton--chart { height:220px; margin:15px 0; }
@keyframes home-shimmer { to { transform:translateX(100%); } }
@media (max-width:1199.98px) { .home-metrics { grid-template-columns:repeat(2,minmax(0,1fr)); }.home-grid,.home-grid--agenda { grid-template-columns:minmax(0,1fr); } }
@media (min-width:992px) and (max-width:1199.98px) { .home-hero { grid-template-columns:minmax(0,1fr) minmax(280px,350px); }.home-hero__summary { grid-column:2; } }
@media (max-width:991.98px) { .home-hero { grid-template-columns:minmax(0,1fr); min-height:0; padding:30px; }.home-hero__copy { grid-column:1; }.home-hero__summary { grid-column:1; max-width:480px; }.home-shortcuts { grid-template-columns:repeat(2,minmax(0,1fr)); margin-left:14px; margin-right:14px; } }
@media (max-width:575.98px) {
  .clinic-home { padding:0 0 24px; }.home-hero { gap:24px; padding:24px 20px 30px; border-radius:18px; }.home-hero h1 { font-size:25px; }
  .home-hero__actions,.home-hero__actions .home-btn { width:100%; }.home-hero__summary { padding:18px; }
  .home-shortcuts { grid-template-columns:minmax(0,1fr); margin:12px 0 24px; }.home-metrics { grid-template-columns:minmax(0,1fr); }
  .home-section__head,.home-panel__head--row { align-items:flex-start; flex-direction:column; }.home-refresh { width:100%; justify-content:center; }
  .home-panel { padding:18px; border-radius:16px; }.home-revenue-total { align-items:flex-start; }
  .home-schedule__item { grid-template-columns:42px 8px minmax(0,1fr); }.home-schedule__item em { display:none; }.home-doctors__item em { font-size:0; }
  .home-empty--horizontal { align-items:flex-start; flex-direction:column; }
}
@media (prefers-reduced-motion:reduce) { .home-btn,.home-shortcut,.home-focus__item { transition:none; }.home-skeleton::after { animation:none; } }
</style>
